import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { A2pCampaignPayload } from '@/apis/business/setting/phoneNumbers/types';

export type OptInMethod = 'website_public' | 'website_not_public' | 'printed' | 'verbal' | 'pos';

export type CampaignFormValues = A2pCampaignPayload & {
  privacy_verified: boolean;
  terms_verified: boolean;
  optin_method: OptInMethod;
  optin_page_url: string;
  optin_script: string;
  optin_verified: boolean;
  pos_description: string;
};

export type CampaignFormStep = 'type' | 'consent' | 'samples' | 'attributes' | 'compliances';

export const CAMPAIGN_STEPS: { id: CampaignFormStep; label: string }[] = [
  { id: 'type', label: 'Campaign Type' },
  { id: 'consent', label: 'Consent & Details' },
  { id: 'samples', label: 'Sample Messages' },
  { id: 'attributes', label: 'Attributes' },
  { id: 'compliances', label: 'Compliances' }
];

export const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  LOW_VOLUME: 'Low Volume Mixed',
  MIXED: 'Mixed'
};

export const campaignTypeLabel = (type?: string | null) => CAMPAIGN_TYPE_LABELS[type || ''] || type || '';

export const defaultCampaignName = (brandName: string, type: string) =>
  [brandName.trim(), campaignTypeLabel(type)].filter(Boolean).join(' ') || campaignTypeLabel(type) || type;

export const DEFAULT_USECASES = ['2FA', 'ACCOUNT_NOTIFICATION'];

export const USECASE_OPTIONS = [
  { value: '2FA', label: '2FA' },
  { value: 'ACCOUNT_NOTIFICATION', label: 'Account Notification' },
  { value: 'CUSTOMER_CARE', label: 'Customer Care' },
  { value: 'DELIVERY_NOTIFICATION', label: 'Delivery Notification' },
  { value: 'FRAUD_ALERT', label: 'Fraud Alert Messaging' },
  { value: 'HIGHER_EDUCATION', label: 'Higher Education' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'POLLING_VOTING', label: 'Polling and Voting' },
  { value: 'PUBLIC_SERVICE', label: 'Public Service Announcement' },
  { value: 'SECURITY_ALERT', label: 'Security Alert' }
];

export const OPT_IN_TEMPLATE_URL = 'https://www.kickcall.ai/terms-conditions/a2p';

export const YES_NO_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
];

export const DEFAULT_CAMPAIGN_DESCRIPTION =
  '[Business Name] is a [type of healthcare provider — clinic, dental practice, telehealth platform, etc.] serving [patient/users]. This campaign sends automated, transactional SMS notifications to patients who have opted in, including [list message types — e.g., appointment reminders/confirmations, reschedule notifications, prescription refill alerts, billing/payment reminders, post-visit follow-up]. Patients opt in by [opt-in method], during which they are shown message frequency, applicable data rates, opt-out instructions, and links to our Terms & Conditions and Privacy Policy. This campaign is strictly [transactional/non-promotional — or note if it includes account notifications]. No sensitive health information (PHI) is included in message content.';

export const DEFAULT_MESSAGE_FLOW =
  'Patients/Users provide their mobile number and give consent to receive SMS at [opt-in point — e.g., during patient intake, at appointment scheduling]. The consent language presented reads: "I agree to receive automated appointment and account-related SMS notifications from [Business Name]. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help." Consent is required — the form/process cannot be completed without it. Privacy Policy: [URL] — Terms & Conditions: [URL] — Screenshots: [URL].';
export const DEFAULT_HELP_KEYWORDS = 'HELP, INFO';
export const DEFAULT_OPTIN_KEYWORDS = 'START, YES';
export const DEFAULT_OPTOUT_KEYWORDS = 'STOP, STOPALL, UNSUBSCRIBE, CANCEL, END, QUIT';

export const PRIVACY_POLICY_TEMPLATE_URL = 'https://www.kickcall.ai/privacy-policy';

export const BORDER_COLOR = '#E5E7EB';
export const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px', px: 2.5 } as const;
export const outlinedPillSx = {
  ...pillSx,
  borderColor: BORDER_COLOR,
  color: 'text.primary',
  bgcolor: '#fff',
  '&:hover': { borderColor: BORDER_COLOR, bgcolor: '#F9FAFB' }
} as const;
export const shrinkLabel = { shrink: true };

export const DEFAULT_CAMPAIGN_VALUES: CampaignFormValues = {
  name: '',
  campaign_type: 'LOW_VOLUME',
  vertical: '',
  description: DEFAULT_CAMPAIGN_DESCRIPTION,
  sample1: '',
  sample2: '',
  sample3: '',
  sample4: '',
  sample5: '',
  message_flow: DEFAULT_MESSAGE_FLOW,
  help_message: '',
  help_keywords: DEFAULT_HELP_KEYWORDS,
  optin_message: '',
  optin_keywords: DEFAULT_OPTIN_KEYWORDS,
  optout_message: '',
  optout_keywords: DEFAULT_OPTOUT_KEYWORDS,
  subscriber_opt_in: true,
  subscriber_opt_out: true,
  subscriber_help: true,
  embedded_link: false,
  embedded_phone: false,
  number_pool: false,
  age_gated: false,
  direct_lending: false,
  affiliate_marketing: false,
  auto_renewal: true,
  privacy_policy_link: '',
  terms_and_conditions_link: '',
  usecases: DEFAULT_USECASES,
  privacy_verified: false,
  terms_verified: false,
  optin_method: 'website_public',
  optin_page_url: 'https://yoursite.com/sms-sign.up',
  optin_script: 'Auto-drafted script — edit as needed...',
  optin_verified: false,
  pos_description:
    'e.g. Customers fill out a consent card at the front desk and check a box to opt in to SMS reminders.'
};

export type StepFormProps = {
  control: Control<CampaignFormValues>;
  watch: UseFormWatch<CampaignFormValues>;
  setValue: UseFormSetValue<CampaignFormValues>;
  lockType?: boolean;
  lockBooleans?: boolean;
  brandName?: string;
};

export const sampleHasBrand = (text: string, brandName: string) =>
  Boolean(brandName.trim()) && text.toLowerCase().includes(brandName.trim().toLowerCase());

export const sampleHasOptOut = (text: string) => /\bstop\b/i.test(text) || /unsubscribe/i.test(text);

export const brandOptOutMessage = (brandName: string) =>
  `<${brandName || 'Brand Name'}>: You have successfully been unsubscribed. You will not receive any more messages from this number.\nReply STOP`;

export const brandHelpMessage = (brandName: string, email?: string) =>
  `<${brandName || 'Brand Name'}>: For assistance contact ${email ? `< ${email}>` : '< support email>'}. Msg & data rates may apply. Reply STOP to unsubscribe.`;

/** Prefill Sample Messages from typical caller SMS (agent Send SMS) patterns. */
export const defaultSampleMessages = (brandName: string) => {
  const brand = brandName.trim() || 'Brand Name';
  return {
    sample1: `${brand}: Thank you for confirming your appointment with us. We look forward to seeing you soon! Reply STOP to unsubscribe.`,
    sample2: `${brand}: Reminder — your appointment is tomorrow at 10:00 AM. Reply C to confirm or R to reschedule. Reply STOP to unsubscribe.`,
    sample3: `${brand}: Your appointment has been rescheduled. Check your confirmation for the new time. Reply STOP to unsubscribe.`,
    sample4: `${brand}: A payment reminder is available for your account. Msg & data rates may apply. Reply STOP to unsubscribe.`,
    sample5: `${brand}: Thanks for visiting us. Reply STOP to unsubscribe.`
  };
};
